import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import LanguageSwitcher from "./LanguageSwitcher";

const Header = ({ siteSettings }) => {
  const { t } = useI18n();
  const { user } = useAuth();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const links = [
    { to: "/", label: t("nav.home"), exact: true },
    { to: "/about", label: t("nav.about") },
    { to: "/export-commodities", label: t("nav.export") },
    { to: "/import-products", label: t("nav.import") },
    { to: "/services", label: t("nav.services") },
    { to: "/catalogue", label: t("nav.catalogue") },
    { to: "/news", label: t("nav.news") },
    { to: "/contact", label: t("nav.contact") },
  ];

  const logoSrc = siteSettings?.logos?.main;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled ? "glass-nav" : "bg-[#0A1128]"
      }`}
    >
      <div className="container-nakia">
        <div className="flex items-center justify-between h-[72px] md:h-[88px]">
          {/* Logo */}
          <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 shrink-0">
            {logoSrc ? (
              <img
                src={logoSrc}
                alt="Nakia"
                className="h-[45px] sm:h-[55px] md:h-[64px] lg:h-[72px] w-auto object-contain"
              />
            ) : (
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 md:w-12 md:h-12 border border-[#D4AF37] flex items-center justify-center rounded-sm">
                  <span className="font-display text-[#D4AF37] text-xl md:text-2xl">N</span>
                </div>
                <div className="leading-none">
                  <div className="font-display text-white text-xl md:text-2xl tracking-[0.3em]">NAKIA</div>
                  <div className="text-[10px] md:text-[11px] text-[#D4AF37]/80 tracking-[0.35em] mt-1">GLOBAL SEJAHTERA</div>
                </div>
              </div>
            )}
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-7 xl:gap-9">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.exact}
                data-testid={`nav-${l.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `text-[13px] uppercase tracking-[0.18em] font-medium transition-colors duration-300 ${
                    isActive
                      ? "text-[#D4AF37]"
                      : "text-white/85 hover:text-[#D4AF37]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* Right cluster */}
          <div className="flex items-center gap-3 md:gap-5">
            <LanguageSwitcher />
            {user?.is_admin ? (
              <Link
                to="/admin"
                data-testid="header-admin-link"
                className="hidden md:inline-flex btn-outline-gold !px-4 !py-2 !text-[11px]"
              >
                {t("nav.admin")}
              </Link>
            ) : null}

            {/* Mobile toggle */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen((s) => !s)}
              className="lg:hidden text-white p-2"
              aria-label="Menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-[#0A1128] border-t border-white/10">
          <nav className="container-nakia py-6 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.exact}
                data-testid={`mobile-nav-${l.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `py-3 text-[13px] uppercase tracking-[0.18em] font-medium border-b border-white/5 ${
                    isActive ? "text-[#D4AF37]" : "text-white/85"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {user?.is_admin && (
              <Link
                to="/admin"
                data-testid="mobile-admin-link"
                className="mt-4 btn-outline-gold w-full"
              >
                {t("nav.admin")}
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
